import { useState, useEffect, useCallback, useRef } from 'react';
import ActivityHistoryService from '@/services/activityHistoryService';
import {
  ActivityHistoryDocument,
  ActivityHistoryQueryOptions,
  ActivityHistoryFilter,
  ActivityHistoryStats,
  RestoreDataOptions,
  ShareBookActionType
} from '@/types/activityHistory';

interface UseActivityHistoryOptions {
  shareBookId?: string;
  enableRealtime?: boolean;
  initialLimit?: number;
}

interface UseActivityHistoryReturn {
  // 데이터
  histories: ActivityHistoryDocument[];
  stats: ActivityHistoryStats | null;
  
  // 로딩 상태
  loading: boolean;
  loadingMore: boolean;
  loadingStats: boolean;
  
  // 에러 상태
  error: string | null;
  
  // 페이지네이션
  hasMore: boolean;
  lastDoc: any | null;
  
  // 액션들
  loadHistories: (options?: ActivityHistoryQueryOptions) => Promise<void>;
  loadMoreHistories: () => Promise<void>;
  loadStats: () => Promise<void>;
  refresh: () => Promise<void>;
  restoreData: (options: RestoreDataOptions) => Promise<void>;
  
  // 필터링
  applyFilter: (filter: ActivityHistoryFilter) => Promise<void>;
  clearFilter: () => Promise<void>;
  
  // 현재 필터
  currentFilter: ActivityHistoryFilter | null;
}

export function useActivityHistory(options: UseActivityHistoryOptions = {}): UseActivityHistoryReturn {
  const { shareBookId, enableRealtime = false, initialLimit = 30 } = options;
  
  // 상태
  const [histories, setHistories] = useState<ActivityHistoryDocument[]>([]);
  const [stats, setStats] = useState<ActivityHistoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any | null>(null);
  const [currentFilter, setCurrentFilter] = useState<ActivityHistoryFilter | null>(null);
  
  // 서비스 인스턴스
  const activityHistoryService = useRef(new ActivityHistoryService());
  const unsubscribeRef = useRef<(() => void) | null>(null);
  
  // 초기 데이터 로드
  const loadHistories = useCallback(async (queryOptions?: ActivityHistoryQueryOptions) => {
    if (!shareBookId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const options = { limitCount: initialLimit, ...queryOptions };
      const result = currentFilter 
        ? await activityHistoryService.current.getFilteredActivityHistory(shareBookId, currentFilter, options)
        : await activityHistoryService.current.getActivityHistory(shareBookId, options);
      
      setHistories(result.histories);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err: any) {
      setError(err.message || '활동 기록을 불러오는 중 오류가 발생했습니다.');
      console.error('활동 기록 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [shareBookId, initialLimit, currentFilter]);
  
  // 더 많은 데이터 로드
  const loadMoreHistories = useCallback(async () => {
    if (!hasMore || loadingMore || !shareBookId) return;
    
    try {
      setLoadingMore(true);
      
      const options = { lastDoc, limitCount: initialLimit };
      const result = currentFilter 
        ? await activityHistoryService.current.getFilteredActivityHistory(shareBookId, currentFilter, options)
        : await activityHistoryService.current.getActivityHistory(shareBookId, options);
      
      setHistories(prev => [...prev, ...result.histories]);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err: any) {
      setError(err.message || '더 많은 활동 기록을 불러오는 중 오류가 발생했습니다.');
      console.error('활동 기록 더 로드 실패:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [shareBookId, lastDoc, hasMore, loadingMore, initialLimit, currentFilter]);
  
  // 통계 로드
  const loadStats = useCallback(async () => {
    if (!shareBookId) return;
    
    try {
      setLoadingStats(true);
      setError(null);
      
      const result = await activityHistoryService.current.getActivityHistoryStats(shareBookId);
      setStats(result);
    } catch (err: any) {
      setError(err.message || '활동 기록 통계를 불러오는 중 오류가 발생했습니다.');
      console.error('활동 기록 통계 로드 실패:', err);
    } finally {
      setLoadingStats(false);
    }
  }, [shareBookId]);
  
  // 새로고침
  const refresh = useCallback(async () => {
    setLastDoc(null);
    setHasMore(true);
    await loadHistories();
  }, [loadHistories]);
  
  // 데이터 복원
  const restoreData = useCallback(async (options: RestoreDataOptions) => {
    try {
      setError(null);
      await activityHistoryService.current.restoreData(options);
      
      // 복원 후 목록 새로고침
      await refresh();
      
      // 통계도 새로고침
      await loadStats();
    } catch (err: any) {
      setError(err.message || '데이터 복원 중 오류가 발생했습니다.');
      console.error('데이터 복원 실패:', err);
      throw err;
    }
  }, [refresh, loadStats]);
  
  // 필터 적용
  const applyFilter = useCallback(async (filter: ActivityHistoryFilter) => {
    setCurrentFilter(filter);
    setLastDoc(null);
    setHasMore(true);
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await activityHistoryService.current.getFilteredActivityHistory(
        shareBookId!, 
        filter, 
        { limitCount: initialLimit }
      );
      
      setHistories(result.histories);
      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err: any) {
      setError(err.message || '필터 적용 중 오류가 발생했습니다.');
      console.error('필터 적용 실패:', err);
    } finally {
      setLoading(false);
    }
  }, [shareBookId, initialLimit]);
  
  // 필터 초기화
  const clearFilter = useCallback(async () => {
    setCurrentFilter(null);
    setLastDoc(null);
    setHasMore(true);
    await loadHistories();
  }, [loadHistories]);
  
  // 실시간 구독 설정
  useEffect(() => {
    if (!shareBookId || !enableRealtime) return;
    
    // 기존 구독 해제
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    
    // 새로운 구독 시작
    unsubscribeRef.current = activityHistoryService.current.subscribeToActivityHistory(
      shareBookId,
      (newHistories) => {
        setHistories(newHistories);
      },
      { limitCount: initialLimit }
    );
    
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [shareBookId, enableRealtime, initialLimit]);
  
  // 초기 로드
  useEffect(() => {
    if (shareBookId) {
      loadHistories();
      loadStats();
    }
  }, [shareBookId, loadHistories, loadStats]);
  
  // 컴포넌트 언마운트 시 구독 해제
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);
  
  return {
    // 데이터
    histories,
    stats,
    
    // 로딩 상태
    loading,
    loadingMore,
    loadingStats,
    
    // 에러 상태
    error,
    
    // 페이지네이션
    hasMore,
    lastDoc,
    
    // 액션들
    loadHistories,
    loadMoreHistories,
    loadStats,
    refresh,
    restoreData,
    
    // 필터링
    applyFilter,
    clearFilter,
    
    // 현재 필터
    currentFilter
  };
}

// 특정 액션 타입별 활동 기록을 조회하는 훅
export function useActivityHistoryByAction(
  shareBookId: string,
  actionType: ShareBookActionType,
  options: UseActivityHistoryOptions = {}
) {
  const { histories, loading, error, loadHistories, refresh } = useActivityHistory({
    ...options,
    shareBookId
  });
  
  const filteredHistories = histories.filter(history => history.actionType === actionType);
  
  const loadActionHistories = useCallback(async () => {
    await loadHistories({ actionType });
  }, [loadHistories, actionType]);
  
  useEffect(() => {
    if (shareBookId) {
      loadActionHistories();
    }
  }, [shareBookId, loadActionHistories]);
  
  return {
    histories: filteredHistories,
    loading,
    error,
    refresh: loadActionHistories
  };
}

// 복원 가능한 활동 기록만 조회하는 훅
export function useRestorableActivityHistory(
  shareBookId: string,
  options: UseActivityHistoryOptions = {}
) {
  const { histories, loading, error, applyFilter, clearFilter } = useActivityHistory({
    ...options,
    shareBookId
  });
  
  const restorableHistories = histories.filter(history => history.metadata.restorable);
  
  const loadRestorableHistories = useCallback(async () => {
    await applyFilter({ restorable: true });
  }, [applyFilter]);
  
  useEffect(() => {
    if (shareBookId) {
      loadRestorableHistories();
    }
  }, [shareBookId, loadRestorableHistories]);
  
  return {
    histories: restorableHistories,
    loading,
    error,
    refresh: loadRestorableHistories,
    clearFilter
  };
}
